import { useMemo } from 'react';

import {
  getGenericLine,
  getLineCategory,
  hasBeneficiaries,
  hasMedical,
  hasProperty,
  hasRisk
} from '@utils/helper-utils';

import {
  amendmentMotorValidationSchema,
  bankersValidationSchema,
  beneficiariesValidationSchema,
  costChargesCancellationValidationSchema,
  costChargesValidationSchema,
  coversCancellationValidationSchema,
  coversValidationSchema,
  descriptionOfRiskValidationSchema,
  expatValidationSchema,
  insuredDetailsValidationSchema,
  insuredsDetailsValidationSchema,
  insuredsValidationSchema,
  marineHullValidationSchema,
  marineValidationSchema,
  medicalDetailsValidationSchema,
  medicalInsuredsValidationSchema,
  payerOwnerValidationSchema,
  propertyValidationSchema,
  travelDetailsValidationSchema
} from '../validations';
import { amendmentDetailsValidationSchema } from '../validations/amendmentDetails.validations';

import { GenericLines, LineCategory } from 'src/enums';

export function useAmendmentValidation(
  status: string,
  lineCode: string,
  beneficiaryMaxNumber: number,
  amendmentType: string,
  amendmentSubTypeCode?: string,
  policyType?: string
) {
  return useMemo(() => {
    const hasMedicalLine = hasMedical(lineCode);
    const isGroupMedical =
      getLineCategory(lineCode) === LineCategory.Generic &&
      getGenericLine(lineCode) === GenericLines.MEDICAL_GROUP;
    const isGroupPolicy = policyType === 'GROUP';
    const isCertificatePolicy = policyType === 'CERTIFICATE';
    const showInsuredsWidget =
      !['19', '48', '6'].includes(lineCode) || !isGroupPolicy;

    const isMedical = getLineCategory(lineCode) === LineCategory.Medical;
    const isTravel = getLineCategory(lineCode) === LineCategory.Travel;

    // Common validation schemas for all line types
    let commonSchemas = amendmentDetailsValidationSchema(
      amendmentType,
      isGroupPolicy
    ).concat(costChargesValidationSchema(hasMedicalLine, amendmentType));
    if (!isGroupPolicy && !isGroupMedical) {
      commonSchemas = commonSchemas.concat(
        payerOwnerValidationSchema(isCertificatePolicy)
      );
    }
    if (showInsuredsWidget) {
      commonSchemas = commonSchemas.concat(
        insuredsValidationSchema(lineCode, isCertificatePolicy)
      );
    }

    // Standard Policy Covers widget is hidden for lines 6 / 19 / 48 (endorsement spec)
    let base = hasMedicalLine
      ? commonSchemas
      : coversValidationSchema(
          lineCode,
          (row: any) =>
            row?.isEditable === true &&
            !['CLOSED_WON', 'CLOSED_LOST'].includes(status),
          amendmentType
        ).concat(commonSchemas);

    const isEarlyLeaversGroupMedical =
      isGroupMedical && amendmentSubTypeCode === 'EC10';
    const isCertificateGroupMedical =
      isGroupMedical && amendmentSubTypeCode === 'EC11';

    if ((isMedical || isTravel) && !isGroupMedical) {
      base = base.concat(medicalInsuredsValidationSchema(lineCode));
    }

    if (isCertificateGroupMedical) {
      base = base
        .concat(medicalDetailsValidationSchema(status))
        .concat(medicalInsuredsValidationSchema(lineCode));
    }

    if (isTravel) {
      base = base.concat(travelDetailsValidationSchema);
    }

    if (getLineCategory(lineCode) === LineCategory.Motor) {
      let schema = base.concat(amendmentMotorValidationSchema(lineCode));

      if (hasBeneficiaries(lineCode)) {
        schema = schema.concat(
          beneficiariesValidationSchema(beneficiaryMaxNumber)
        );
      }

      return schema;
    }

    // Line 6 has beneficiaries; handle EC10 before the generic beneficiaries return
    // so certificate variance fields are validated (same as cancellation insured details).
    if (isEarlyLeaversGroupMedical) {
      return base.concat(insuredsDetailsValidationSchema(true, amendmentType));
    }

    if (hasBeneficiaries(lineCode) && !isGroupMedical) {
      return base.concat(beneficiariesValidationSchema(beneficiaryMaxNumber));
    }

    if (getGenericLine(lineCode) === GenericLines.MARINE) {
      return base.concat(marineValidationSchema());
    }

    if (getGenericLine(lineCode) === GenericLines.MARINE_HULL) {
      return base.concat(marineHullValidationSchema());
    }

    if (getGenericLine(lineCode) === GenericLines.BBB) {
      return base.concat(bankersValidationSchema());
    }

    if (getLineCategory(lineCode) === LineCategory.Expat) {
      return base
        .concat(expatValidationSchema())
        .concat(insuredDetailsValidationSchema());
    }

    if (hasProperty(lineCode)) {
      return base.concat(propertyValidationSchema());
    }

    if (hasRisk(lineCode)) {
      return base.concat(descriptionOfRiskValidationSchema());
    }

    return base;
  }, [
    amendmentType,
    amendmentSubTypeCode,
    lineCode,
    status,
    beneficiaryMaxNumber,
    policyType
  ]);
}

export function useAmendmentCancellationValidation(
  amendmentType: string,
  lineCode: string,
  showPolicyCovers: boolean,
  showInsuredsDetails: boolean,
  showChargesFields: boolean
) {
  return useMemo(() => {
    console.log(insuredsDetailsValidationSchema());
    const commonSchemas = amendmentDetailsValidationSchema(amendmentType)
      .concat(costChargesCancellationValidationSchema(showChargesFields))
      .concat(
        coversCancellationValidationSchema(
          lineCode,
          (row: any) =>
            row?.isEditable === true &&
            !['CLOSED_WON', 'CLOSED_LOST'].includes(status),
          amendmentType
        )
      )
      .concat(insuredsDetailsValidationSchema());
    console.log(commonSchemas);

    return commonSchemas;
  }, [amendmentType, showPolicyCovers, showInsuredsDetails, showChargesFields]);
}
